import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { setSentryUser, clearSentryUser } from '../lib/sentry';

export interface AuthUser {
  userId: string;
  tenantId: string;
  // ADR-023: SUPERADMIN_PLATFORM | TENANT_OWNER | TENANT_ADMIN | TENANT_AUDITOR | MEMBER
  role: string;
  email: string;
  provider: string;
  nombreCompleto?: string | null;
  // Impersonation (prompt 0524) — solo presente cuando el JWT trae el flag.
  impersonating?: boolean;
  originalUserId?: string;
  originalTenantId?: string;
  originalEmail?: string;
}

export interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  isAdmin: boolean;
  isPlatformAdmin: boolean;
  /** True si la sesión actual es impersonation. Driver del banner amarillo. */
  isImpersonating: boolean;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Cierra la impersonation, restaura el JWT original y recarga. */
  endImpersonation: () => Promise<void>;
}

const ORIGINAL_TOKEN_BACKUP_KEY = 'vianet-token-original';

// ADR-023: roles canonicos de admin de sistemas.
// "Admin" agrupa SUPERADMIN_PLATFORM (VALOR·IA) + TENANT_OWNER + TENANT_ADMIN.
// TENANT_AUDITOR no es admin (solo lectura). MEMBER tampoco.
const ADMIN_ROLES = ['SUPERADMIN_PLATFORM', 'TENANT_OWNER', 'TENANT_ADMIN'];

export function isPlatformAdmin(user: AuthUser | null): boolean {
  return user?.role === 'SUPERADMIN_PLATFORM';
}
const SESSION_KEY = 'vianet_auth_user';

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  isAdmin: false,
  isPlatformAdmin: false,
  isImpersonating: false,
  logout: async () => {},
  refresh: async () => {},
  endImpersonation: async () => {},
});

function readCachedUser(): AuthUser | null {
  try {
    const cached = sessionStorage.getItem(SESSION_KEY);
    return cached ? (JSON.parse(cached) as AuthUser) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const cachedOnInit = readCachedUser();
  const [user, setUser] = useState<AuthUser | null>(cachedOnInit);
  const [loading, setLoading] = useState(!cachedOnInit);

  // Si hubo sesión cacheada al montar, hidratar Sentry inmediatamente
  // (antes de que fetchUser termine) para no perder eventos tempranos.
  useEffect(() => {
    if (cachedOnInit) {
      setSentryUser({
        id: cachedOnInit.userId,
        tenantId: cachedOnInit.tenantId,
        email: cachedOnInit.email,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUser = async () => {
    try {
      // Token en localStorage (lo guarda LoginPage tras POST /api/auth/login/local).
      // Necesario porque Vercel rewrites no preservan Set-Cookie cross-subdomain
      // (app.valoriagovernance.com → api.valoriagovernance.com). La cookie
      // `vianet_auth` nunca llega al browser; el token del localStorage sí.
      const token = localStorage.getItem('vianet-token');
      const res = await fetch('/api/auth/me', {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const json = await res.json();
        const userData: AuthUser | null = json.data ?? null;
        setUser(userData);
        if (userData) {
          sessionStorage.setItem(SESSION_KEY, JSON.stringify(userData));
          setSentryUser({
            id: userData.userId,
            tenantId: userData.tenantId,
            email: userData.email,
          });
        } else {
          sessionStorage.removeItem(SESSION_KEY);
          clearSentryUser();
        }
      } else {
        setUser(null);
        sessionStorage.removeItem(SESSION_KEY);
        clearSentryUser();
      }
    } catch {
      setUser(null);
      clearSentryUser();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const logout = async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    setUser(null);
    sessionStorage.removeItem(SESSION_KEY);
    localStorage.removeItem('vianet-token');
    sessionStorage.removeItem(ORIGINAL_TOKEN_BACKUP_KEY);
    clearSentryUser();
    window.location.href = '/login';
  };

  /**
   * Cierra la impersonation activa (prompt 0524).
   * Avisa al backend para auditar el FIN, restaura el JWT original que se guardó
   * en `sessionStorage[ORIGINAL_TOKEN_BACKUP_KEY]` antes de iniciar la
   * impersonation y recarga la app para que el AuthContext rehidrate como
   * usuario original.
   */
  const endImpersonation = async () => {
    const tempToken = localStorage.getItem('vianet-token');
    try {
      // Best-effort — si falla, igualmente restauramos para no dejar al usuario atrapado.
      await fetch('/api/v1/admin/impersonate/end', {
        method: 'POST',
        credentials: 'include',
        headers: tempToken ? { Authorization: `Bearer ${tempToken}` } : {},
      });
    } catch {
      // ignorar — preferimos restaurar siempre.
    }

    const original = sessionStorage.getItem(ORIGINAL_TOKEN_BACKUP_KEY);
    if (original) {
      localStorage.setItem('vianet-token', original);
      sessionStorage.removeItem(ORIGINAL_TOKEN_BACKUP_KEY);
    } else {
      // No hay backup — forzar logout para no quedar en limbo.
      localStorage.removeItem('vianet-token');
    }
    sessionStorage.removeItem(SESSION_KEY);
    window.location.assign('/');
  };

  const refresh = fetchUser;

  const isAdmin = ADMIN_ROLES.includes(user?.role ?? '');
  const platformAdmin = isPlatformAdmin(user);
  const impersonating = !!user?.impersonating;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAdmin,
        isPlatformAdmin: platformAdmin,
        isImpersonating: impersonating,
        logout,
        refresh,
        endImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
